import { EditorContext, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';

import { Extensions } from '@tiptap/core';

import cn from 'classnames';
import { twMerge } from 'tailwind-merge';

import NoCopyPaste from './plugins/NoCopyPaste';
import UniqueId from './plugins/UniqueId';
import Content, { getContentParser } from './plugins/Content';
import CodeBlockPreview from './plugins/coding/CodeBlockPreview';

import MarkdownExtension from './plugins/markdown';
import { memo, MutableRefObject, useEffect, useRef } from 'react';
import { EditorRendererContext } from './Editor';
import Comment from './plugins/comment';
import ReviewerCopilot from './plugins/ReviewerCopilot';

import Alert from './plugins/Alert';
import {
  DEFAULT_TOKENS,
  DefaultTokenConfig,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/MarkdownEditorContext';
import { debounce } from 'lodash';
import { HTMLNode } from './plugins/markdown/nodes/anyhtml';
import TextAlign from '@tiptap/extension-text-align';

interface MarkdownEditorContextProps {
  className?: string;
  children: React.ReactNode;
  extensions?: Extensions;
  content?: string;
  defaultTokens?: DefaultTokenConfig;
  enableMath?: boolean;
  enableHtml?: boolean;
  enableSoftBreaks?: boolean;
  enableSingleDollarTextMath?: boolean;
  enableCodeExecution?: boolean;
  defaultExpandCodeBlocks?: boolean;
  shouldDebounceContentUpdates?: boolean;
  isCoursePreview?: boolean;
}

function MarkdownPreviewContext({
  className,
  children,
  extensions,
  content,
  defaultTokens = DEFAULT_TOKENS,
  enableMath = false,
  enableHtml = false,
  enableSoftBreaks = false,
  enableCodeExecution = false,
  enableSingleDollarTextMath = false,
  defaultExpandCodeBlocks = true,
  shouldDebounceContentUpdates = true,
  isCoursePreview = false,
}: MarkdownEditorContextProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const editor = useEditor(
    {
      content,
      immediatelyRender: true,
      extensions: [
        UniqueId,
        MarkdownExtension.configure({
          enableMath,
          enableHtml,
          enableSoftBreaks,
          enableSingleDollarTextMath,
          boldToken: defaultTokens.boldToken,
          italicToken: defaultTokens.italicToken,
          bulletListToken: defaultTokens.bulletListToken,
          mathToken: defaultTokens.mathToken,
          isCoursePreview,
        }),
        Content.configure({
          type: 'markdown',
        }),
        StarterKit.configure({
          text: false,
          bold: false,
          italic: false,
          bulletList: false,
          listItem: false,
          codeBlock: false,
          paragraph: false,
        }),
        NoCopyPaste,
        Comment.configure({
          classes: {
            base: 'comment hover:cursor-pointer',
            selection: 'bg-warning-100',
            comment: 'bg-primary-100 hover:bg-primary-200',
            selectedComment: 'bg-primary-200',
          },
        }),
        ReviewerCopilot.configure(),
        CodeBlockPreview.configure({
          enableCodeExecution,
          defaultExpanded: defaultExpandCodeBlocks,
        }),
        Alert,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        ...(extensions ?? []),
        ...(enableHtml ? [HTMLNode] : []),
      ],
      // set initial editor options, do not add dynamic options here!
      editorProps: {
        attributes: {
          class: twMerge(
            cn(
              'relative max-w-none [&>pre]:p-2 [&>pre]:rounded-lg focus:outline-none min-h-[60px] text-neutral-800 scale-tailwind wysiwyg wysiwyg-chat wysiwyg-sm',
              className,
            ),
          ),
        },
      },
    },
    [
      enableMath,
      enableHtml,
      enableSoftBreaks,
      enableSingleDollarTextMath,
      enableCodeExecution,
      defaultTokens?.mathToken,
      isCoursePreview,
    ],
  );

  useEffect(
    function updateContent() {
      if (!editor) return;
      const parseContent = getContentParser(editor);
      if (shouldDebounceContentUpdates) {
        const debounceParseContent = debounce(parseContent, 1000);
        debounceParseContent(content, false);
        return () => {
          debounceParseContent.cancel();
        };
      } else {
        parseContent(content, false);
      }
    },
    [editor, content, shouldDebounceContentUpdates],
  );

  if (!editor) {
    return null;
  }

  return (
    <EditorContext.Provider value={{ editor }}>
      <EditorRendererContext.Provider
        value={{ editorRef: editorRef as MutableRefObject<HTMLDivElement> }}
      >
        {children}
      </EditorRendererContext.Provider>
    </EditorContext.Provider>
  );
}

export default memo(MarkdownPreviewContext);
